import { create } from 'zustand'
import axios from 'axios'
import type { User } from '@/types'
import { queryClient } from '@/lib/queryClient'

const authApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  withCredentials: true,
})

const OFFLINE_SESSION_KEY = 'granula.auth.session'

type PersistedSession = {
  accessToken: string | null
  user: User | null
}

function readPersistedSession(): PersistedSession {
  if (typeof window === 'undefined') return { accessToken: null, user: null }
  try {
    const raw = sessionStorage.getItem(OFFLINE_SESSION_KEY)
    if (!raw) return { accessToken: null, user: null }
    const parsed = JSON.parse(raw) as Partial<PersistedSession>
    return {
      accessToken: typeof parsed.accessToken === 'string' ? parsed.accessToken : null,
      user: parsed.user && typeof parsed.user === 'object' ? parsed.user as User : null,
    }
  } catch {
    return { accessToken: null, user: null }
  }
}

function persistSession(session: PersistedSession) {
  if (typeof window === 'undefined') return
  try {
    if (session.accessToken || session.user) {
      sessionStorage.setItem(OFFLINE_SESSION_KEY, JSON.stringify(session))
    } else {
      sessionStorage.removeItem(OFFLINE_SESSION_KEY)
    }
  } catch {
    // Private browsing or blocked storage should not prevent authentication.
  }
}

const initialSession = readPersistedSession()

// Attach the in-memory access token to every authApi request so that
// fetchMe() and any other authApi call works after init() stores the token.
// This must be a lazily-evaluated interceptor (runs per request, not at module
// init), so the circular ref (authApi reads authStore, authStore uses authApi)
// is fine — authStore is always initialized before any request fires.
authApi.interceptors.request.use((config) => {
  try {
    // /auth/refresh and /auth/logout are authenticated by the HttpOnly refresh
    // cookie, not the access token. The backend lists header tokens before cookie
    // tokens (JWT_TOKEN_LOCATION), so attaching the access token here makes those
    // refresh-only endpoints read the wrong token type and reject the request —
    // which silently breaks logout. Never send the access token to them.
    const url = config.url || ''
    const skipAuthHeader = url.includes('/auth/refresh') || url.includes('/auth/logout')
    const token = authStore.getState().accessToken
    if (token && !skipAuthHeader) {
      config.headers = config.headers ?? {}
      config.headers.Authorization = `Bearer ${token}`
    }
  } catch {
    // A malformed interceptor state must not block the request.
  }
  return config
})

// Single-flight refresh. Coalesces every concurrent refresh attempt — StrictMode
// double-invoked init(), parallel 401 retries from the fetch client, multiple
// callers — into ONE in-flight request. The backend rotates on each refresh
// (revokes the old token's jti), so firing several at once makes all-but-one
// present a just-revoked token → 401 → spurious logout. One shared promise
// removes that race. Returns the new access token, or null on failure.
let inflightRefresh: Promise<string | null> | null = null
export function refreshAccessToken(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh
  inflightRefresh = authApi
    .post('/auth/refresh')
    .then((res) => {
      const token = (res.data?.access_token as string) ?? null
      if (token) authStore.getState().setAccessToken(token)
      return token
    })
    .catch(() => null)
    .finally(() => { inflightRefresh = null })
  return inflightRefresh
}

type AuthState = {
  user: User | null
  accessToken: string | null
  initializing: boolean
  setAccessToken: (token: string | null) => void
  setUser: (user: User | null) => void
  clear: () => void
  init: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  fetchMe: () => Promise<void>
}

export const authStore = create<AuthState>((set, get) => ({
  user: initialSession.user,
  accessToken: initialSession.accessToken,
  initializing: true,

  setAccessToken: (token) => {
    set({ accessToken: token })
    persistSession({ accessToken: token, user: get().user })
  },
  setUser: (user) => {
    set({ user })
    persistSession({ accessToken: get().accessToken, user })
  },
  clear: () => {
    // Server-state queries contain user- and organisation-scoped records. Keep
    // them out of the next authenticated session when a browser switches
    // accounts without a full page reload.
    queryClient.clear()
    set({ user: null, accessToken: null })
    persistSession({ accessToken: null, user: null })
  },

  init: async () => {
    // A reload in airplane mode cannot rotate the refresh cookie. Keep the
    // short-lived access token and user from this browser tab so the offline
    // shell can reopen and read the scoped RxDB cache. Once connectivity is
    // restored, the normal refresh path runs again.
    if (!navigator.onLine && get().user && get().accessToken) {
      set({ initializing: false })
      return
    }
    try {
      // Use the refresh cookie to get a fresh access token, via the shared
      // single-flight helper so a StrictMode double-invoke (or a racing 401
      // retry) can't fire two rotating refreshes and log the user out.
      const token = await refreshAccessToken()
      if (token) {
        // refreshAccessToken already stored the token; the interceptor will
        // attach it for this call.
        await get().fetchMe()
      }
    } catch (_) {
      // No valid session — user stays unauthenticated, ProtectedRoute redirects.
    } finally {
      set({ initializing: false })
    }
  },

  login: async (email, password) => {
    const { data } = await authApi.post('/auth/login', { email, password })
    const token = data?.access_token as string
    set({ accessToken: token, user: data?.user })
    persistSession({ accessToken: token, user: data?.user ?? null })
  },

  logout: async () => {
    try { await authApi.post('/auth/logout') } catch (_) {
      // The local session is cleared even when the network is unavailable.
    }
    get().clear()
  },

  fetchMe: async () => {
    const { data } = await authApi.get('/auth/me')
    // /auth/me returns a bare user object. Stay tolerant of the old { user: ... }
    // envelope so a contract change never silently logs the user out.
    const user = data && typeof data === 'object' && 'user' in data
      ? (data as { user: User }).user
      : data as User
    set({ user })
    persistSession({ accessToken: get().accessToken, user })
  },
}))
